import { ProjectStatusPhase, ProjectStatusValue } from '../types/hubspot';

interface ProjectMilestoneTimelineProps {
  phases?: ProjectStatusPhase[] | null;
  projektdokumentationApprovalStatus?: 'draft' | 'in_review' | 'ready' | 'approved' | null;
  compact?: boolean;
  className?: string;
  showMilestoneText?: boolean;
  doneBadgeClassName?: string;
  /** false, wenn für den Deal noch gar kein Projektfortschritt-Snapshot vorliegt (statt "0% erreicht") */
  hasProgressData?: boolean;
}

interface MilestoneDefinition {
  key: 'sales' | 'durchfuehrung' | 'inbetriebnahme' | 'abschlussbericht';
  label: string;
  phaseKey: ProjectStatusPhase['phaseKey'];
}

interface MilestoneState {
  phaseStatus: ProjectStatusValue;
  approvalStatus?: 'draft' | 'in_review' | 'ready' | 'approved' | null;
  completionPercent: number;
  reached: boolean;
  isActive: boolean;
}

interface TimelineState {
  milestones: MilestoneState[];
  progressPercent: number;
  betweenLabel: string;
  done: boolean;
}

const MILESTONES: MilestoneDefinition[] = [
  { key: 'sales', label: 'Sales', phaseKey: 'projektdokumentation' },
  { key: 'durchfuehrung', label: 'Durchführung', phaseKey: 'durchfuehrung' },
  { key: 'inbetriebnahme', label: 'Inbetriebnahme', phaseKey: 'inbetriebnahme' },
  { key: 'abschlussbericht', label: 'Abschlussbericht', phaseKey: 'abschlussbericht' },
];

const NODE_POSITIONS = [0, 33.3333, 66.6666, 100];

function clampPercent(value: number | null | undefined): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Number(value)));
}

function formatBetweenLabel(from: string, to: string): string {
  return `Zwischen ${from} und ${to}`;
}

function inferProjektdokumentationApprovalStatus(
  phase: ProjectStatusPhase | undefined,
  explicitStatus?: 'draft' | 'in_review' | 'ready' | 'approved' | null
): 'draft' | 'in_review' | 'ready' | 'approved' | null {
  if (explicitStatus === 'draft' || explicitStatus === 'in_review' || explicitStatus === 'ready' || explicitStatus === 'approved') {
    return explicitStatus;
  }
  if (!phase) {
    return null;
  }
  if (phase.status === 'completed') {
    return phase.approvedByEmail ? 'approved' : 'ready';
  }
  if (phase.status === 'open') {
    return 'draft';
  }
  if (phase.status === 'in_progress') {
    if (phase.completionPercent >= 100 && (phase.missingRequiredCount || 0) === 0) {
      return 'ready';
    }
    if (phase.completionPercent > 0) {
      return 'in_review';
    }
  }
  return null;
}

export function deriveProjectTimelineState(
  phases?: ProjectStatusPhase[] | null,
  projektdokumentationApprovalStatus?: 'draft' | 'in_review' | 'ready' | 'approved' | null,
  hasProgressData = true
): TimelineState {
  const phaseMap = new Map((phases || []).map((phase) => [phase.phaseKey, phase]));
  const salesPhase = phaseMap.get('projektdokumentation');
  const salesApprovalStatus = inferProjektdokumentationApprovalStatus(
    salesPhase,
    projektdokumentationApprovalStatus
  );
  const downstreamPhaseStarted = MILESTONES.slice(1).some((milestone) => {
    const status = phaseMap.get(milestone.phaseKey)?.status;
    return status === 'in_progress' || status === 'completed';
  });

  const milestoneStates: MilestoneState[] = MILESTONES.map((milestone, index) => {
    const source = phaseMap.get(milestone.phaseKey);
    const phaseStatus: ProjectStatusValue = source?.status || 'open';
    const completionPercent = clampPercent(source?.completionPercent);

    if (index === 0) {
      const salesReached = phaseStatus === 'completed' || salesApprovalStatus === 'approved' || downstreamPhaseStarted;
      return {
        phaseStatus,
        approvalStatus: salesApprovalStatus,
        completionPercent,
        reached: salesReached,
        isActive: hasProgressData && !salesReached,
      };
    }

    return {
      phaseStatus,
      completionPercent,
      reached: phaseStatus === 'completed',
      isActive: phaseStatus === 'in_progress',
    };
  });

  const done = milestoneStates[3]?.phaseStatus === 'completed';
  if (done) {
    return {
      milestones: milestoneStates,
      progressPercent: 100,
      betweenLabel: 'Alle Meilensteine abgeschlossen',
      done: true,
    };
  }

  const salesReached = milestoneStates[0]?.reached;
  if (!salesReached) {
    if (!hasProgressData) {
      return {
        milestones: milestoneStates,
        progressPercent: 0,
        betweenLabel: 'Noch kein Fortschritt verfügbar',
        done: false,
      };
    }
    const salesApproval = milestoneStates[0]?.approvalStatus;
    return {
      milestones: milestoneStates,
      progressPercent: 0,
      betweenLabel:
        salesApproval === 'ready'
          ? 'Sales freigabefähig'
          : salesApproval === 'in_review'
            ? 'Sales in Prüfung'
            : 'Sales in Arbeit',
      done: false,
    };
  }

  const inProgressIndex = milestoneStates.findIndex((state, index) => index > 0 && state.phaseStatus === 'in_progress');
  if (inProgressIndex > 0) {
    const left = NODE_POSITIONS[inProgressIndex - 1];
    const right = NODE_POSITIONS[inProgressIndex];
    const completion = milestoneStates[inProgressIndex].completionPercent / 100;
    const normalizedCompletion = completion > 0 ? completion : 0.15;

    return {
      milestones: milestoneStates,
      progressPercent: left + (right - left) * normalizedCompletion,
      betweenLabel: formatBetweenLabel(MILESTONES[inProgressIndex - 1].label, MILESTONES[inProgressIndex].label),
      done: false,
    };
  }

  const lastReachedIndex = milestoneStates.reduce((latest, state, index) => {
    if (state.reached) return index;
    return latest;
  }, -1);

  if (lastReachedIndex >= 0 && lastReachedIndex < MILESTONES.length - 1) {
    return {
      milestones: milestoneStates,
      progressPercent: NODE_POSITIONS[lastReachedIndex],
      betweenLabel: `${MILESTONES[lastReachedIndex].label} erreicht`,
      done: false,
    };
  }

  return {
    milestones: milestoneStates,
    progressPercent: 0,
    betweenLabel: 'Sales in Arbeit',
    done: false,
  };
}

function getNodeTone(state: MilestoneState): string {
  if (state.approvalStatus === 'ready') return 'border-blue-400 bg-blue-400 text-white';
  if (state.reached) return 'border-emerald-500 bg-emerald-500 text-white';
  if (state.isActive) return 'border-amber-400 bg-amber-400 text-slate-900';
  return 'border-slate-300 bg-white text-slate-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300';
}

function getMilestoneTooltipText(index: number, state: MilestoneState): string {
  const label = MILESTONES[index]?.label || '';
  if (index === 0 && state.approvalStatus === 'ready') return `${label}: freigabefähig`;
  if (index === 0 && state.approvalStatus === 'in_review') return `${label}: in Prüfung`;
  if (state.reached) return `${label}: erreicht`;
  if (state.isActive) return `${label}: in Arbeit`;
  return `${label}: offen`;
}

function getMilestoneStatusLabel(state: MilestoneState): string {
  if (state.approvalStatus === 'ready') return 'Freigabefähig';
  if (state.approvalStatus === 'in_review') return 'In Prüfung';
  if (state.reached) return 'Erreicht';
  if (state.isActive) return 'In Arbeit';
  return 'Offen';
}

export default function ProjectMilestoneTimeline({
  phases,
  projektdokumentationApprovalStatus,
  compact = false,
  className = '',
  showMilestoneText = false,
  doneBadgeClassName = '',
  hasProgressData = true,
}: ProjectMilestoneTimelineProps) {
  const state = deriveProjectTimelineState(phases, projektdokumentationApprovalStatus, hasProgressData);
  const markerOffset = compact ? 5 : 6;

  return (
    <div className={`rounded-2xl border border-slate-200/80 bg-white/80 px-3 py-3 dark:border-white/10 dark:bg-slate-950/60 ${className}`}>
      <div className="flex items-center">
        <div className="relative min-w-0 flex-1 pt-2">
          <div className="absolute left-0 right-0 top-[10px] h-1 rounded-full bg-slate-200 dark:bg-slate-700" />
          <div
            className="absolute left-0 top-[10px] h-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-orange-500"
            style={{ width: `${state.progressPercent}%` }}
          />
          {!state.done && (
            <span
              className={`absolute top-[10px] -translate-y-1/2 rounded-full border-2 border-white bg-slate-900 shadow ${compact ? 'h-2.5 w-2.5' : 'h-3 w-3'}`}
              style={{ left: `calc(${state.progressPercent}% - ${markerOffset}px)` }}
            />
          )}

          <div className="relative grid grid-cols-4">
            {state.milestones.map((milestone, index) => (
              <div key={MILESTONES[index].key} className="group relative flex flex-col items-center text-center">
                <span
                  title={getMilestoneTooltipText(index, milestone)}
                  className={`flex items-center justify-center rounded-full border-2 font-semibold ${compact ? 'h-4 w-4 text-[9px]' : 'h-5 w-5 text-[10px]'} ${getNodeTone(milestone)}`}
                >
                  {milestone.reached ? '✓' : index + 1}
                </span>
                {showMilestoneText ? (
                  <div className="mt-2 space-y-0.5">
                    <p className="text-[10px] font-semibold leading-tight text-slate-700 dark:text-slate-200">
                      {MILESTONES[index].label}
                    </p>
                    <p className="text-[10px] leading-tight text-slate-500 dark:text-slate-400">
                      {getMilestoneStatusLabel(milestone)}
                    </p>
                  </div>
                ) : (
                  <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-900 px-2 py-1 text-[10px] font-medium text-white shadow-md group-hover:block dark:bg-slate-100 dark:text-slate-900">
                    {getMilestoneTooltipText(index, milestone)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {state.done && (
          <div className={`-ml-1 z-30 shrink-0 rounded-full border-2 border-emerald-500 bg-emerald-500 text-white shadow ${compact ? 'h-8 w-8' : 'h-10 w-10'} flex items-center justify-center ${doneBadgeClassName}`.trim()}>
            <svg className={compact ? 'h-4 w-4' : 'h-5 w-5'} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.42l2.293 2.294 6.493-6.494a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>

      <p className={`mt-2 font-medium ${compact ? 'text-[11px]' : 'text-xs'} text-slate-500 dark:text-slate-400`}>
        {state.betweenLabel}
      </p>
    </div>
  );
}
